import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Select,
  Stack,
  Box,
  Input,
  Divider,
  useToast,
} from '@chakra-ui/react';
import xhr from 'xhr';
import dayjs from 'dayjs';
import { STUDENT_SCHOOL_FILTER_OPTIONS } from 'utils/studentSchools';

function AttendanceReportModal({ isOpen, onClose, filters, students = [] }) {
  const toast = useToast();

  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [generating, setGenerating] = useState(false);

  const [gradeName, setGradeName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedPeriod('');
      setDateFrom('');
      setDateTo('');
      setGenerating(false);
      setGradeName('');
      setGroupName('');
      setSelectedSchool('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadContextNames = async () => {
      let nextGradeName = filters?.program || '';
      let nextGroupName = filters?.group || '';

      try {
        if (filters?.program) {
          const gradeRes = await xhr(`/grades/${filters.program}`);
          const gradeData = gradeRes?.data?.data || gradeRes?.data || gradeRes;
          const gradeAttrs = gradeData?.attributes || gradeData || {};

          nextGradeName =
            gradeAttrs?.name ||
            gradeAttrs?.title ||
            gradeAttrs?.label ||
            nextGradeName;
        }
      } catch (error) {
        console.error('Error cargando nombre del grade:', error);
      }

      try {
        if (filters?.group) {
          const groupRes = await xhr(`/class-groups/${filters.group}`);
          const groupData = groupRes?.data?.data || groupRes?.data || groupRes;
          const groupAttrs = groupData?.attributes || groupData || {};

          nextGroupName =
            groupAttrs?.name ||
            groupAttrs?.title ||
            groupAttrs?.label ||
            nextGroupName;
        }
      } catch (error) {
        console.error('Error cargando nombre del grupo:', error);
      }

      if (!cancelled) {
        setGradeName(nextGradeName);
        setGroupName(nextGroupName);
      }
    };

    loadContextNames();

    return () => {
      cancelled = true;
    };
  }, [isOpen, filters?.program, filters?.group]);

  const normalizeStudents = (rows = []) => {
    return (rows || [])
      .map((row) => {
        const st = row?.student || row || {};
        const user = st?.user || {};

        return {
          id: st?.id,
          name:
            `${user?.last_name || ''} ${user?.first_name || ''}`.trim() ||
            st?.full_name ||
            `Estudiante ${st?.id || ''}`,
          school:
            st?.school ||
            '',
        };
      })
      .filter((student) => student.id);
  };

  const studentsInPage = useMemo(() => normalizeStudents(students), [students]);

  const loadAllFilteredStudents = async () => {
    const params = new URLSearchParams({
      program_type: filters.program_type || '',
      page: '1',
      pageSize: '999999',
    });

    if (filters.program) params.set('program', filters.program);
    if (filters.semester) params.set('semester', filters.semester);
    if (filters.group) params.set('group', filters.group);
    if (filters.status) params.set('status', filters.status);
    if (filters.period) params.set('period', filters.period);

    const res = await xhr(`/students/generalFilter?${params.toString()}`);
    const rows = res?.data?.students || [];
    return normalizeStudents(rows);
  };

  const handleDownloadExcel = async () => {
    if (!filters?.program) {
      toast({
        title: 'Falta el grado',
        description: 'No se encontró el grado actual en los filtros.',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (!filters?.group) {
      toast({
        title: 'Falta el grupo',
        description: 'No se encontró el grupo actual en los filtros.',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (!selectedSchool) {
      toast({
        title: 'Falta el colegio',
        description: 'Selecciona el colegio que quieres imprimir.',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (!selectedPeriod || !dateFrom || !dateTo) {
      toast({
        title: 'Datos incompletos',
        description: 'Selecciona período, fecha inicial y fecha final.',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (dayjs(dateFrom).isAfter(dayjs(dateTo))) {
      toast({
        title: 'Rango inválido',
        description: 'La fecha inicial no puede ser mayor que la final.',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    try {
      setGenerating(true);

      const allStudents = await loadAllFilteredStudents();
      const filteredStudents = allStudents.filter(
        (student) =>
          String(student.school || '').trim().toLowerCase() ===
          String(selectedSchool || '').trim().toLowerCase()
      );

      if (!filteredStudents.length) {
        toast({
          title: 'Sin estudiantes',
          description: 'No hay estudiantes de ese colegio para los filtros actuales.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const payload = {
        gradeId: filters.program,
        classGroupId: filters.group,
        period: selectedPeriod,
        dateFrom,
        dateTo,
        gradeName: gradeName || filters?.program || '',
        groupName: groupName || filters?.group || '',
        school: selectedSchool,
        students: filteredStudents,
      };

      const response = await xhr.post(
        '/student-attendances/export-attendance-report',
        payload,
        { responseType: 'blob' }
      );

      const blob =
        response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `asistencia-${selectedPeriod}-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Excel generado',
        description: 'El archivo de asistencia fue descargado correctamente.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error('Error descargando Excel de asistencia:', error);

      toast({
        title: 'Error',
        description: 'No se pudo generar el Excel de asistencia.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Descargar asistencia</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Stack spacing={5}>
            <Box>
              <Text fontWeight="semibold" mb={1}>
                Contexto actual
              </Text>
              <Text fontSize="sm" color="gray.600">
                Grado: {gradeName || filters?.program || 'N/A'}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Grupo: {groupName || filters?.group || 'N/A'}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Estudiantes en página: {studentsInPage.length}
              </Text>
            </Box>

            <Divider />

            <Box>
              <Text mb={2} fontWeight="semibold">
                Colegio
              </Text>
              <Select
                placeholder="Selecciona un colegio"
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
              >
                {STUDENT_SCHOOL_FILTER_OPTIONS.map((school) => (
                  <option key={school.value} value={school.value}>
                    {school.label}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text mb={2} fontWeight="semibold">
                Período
              </Text>
              <Select
                placeholder="Selecciona un período"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <option value="primer">Primer</option>
                <option value="segundo">Segundo</option>
                <option value="tercer">Tercer</option>
                <option value="cuarto">Cuarto</option>
              </Select>
            </Box>

            <Box>
              <Text mb={2} fontWeight="semibold">
                Rango de fechas
              </Text>
              <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
                <Box flex="1">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Fecha inicial
                  </Text>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Box>

                <Box flex="1">
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Fecha final
                  </Text>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Box>
              </Stack>
            </Box>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button mr={3} onClick={onClose} isDisabled={generating}>
            Cerrar
          </Button>
          <Button
            colorScheme="green"
            onClick={handleDownloadExcel}
            isLoading={generating}
            loadingText="Generando Excel"
          >
            Descargar Excel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AttendanceReportModal;
